/**
 * Utilities for transforming Supabase storage URLs
 *
 * Files are served from natestoker.com/files/<path>. On Hostinger, .htaccess
 * sends that to public/_media/serve.php, which fetches the file from Supabase
 * Storage once, caches it on the server, and serves it (resized when ?w= is set).
 * Script-capable types (HTML, JS, SVG) are redirected to the Supabase files
 * function so they never run on the site's own origin.
 */

const SUPABASE_PROJECT_REF = 'ykybzysdircfkztetkbv';
// "hostinger" (default): resize through natestoker.com/files/<path>?w=<width>
// "on": Supabase /render/image (needs a paid Supabase plan)
// "off": always serve originals
const IMAGE_TRANSFORMS = (import.meta.env.VITE_IMAGE_TRANSFORMS as string | undefined) || 'hostinger';
const FILES_BASE_URL = 'https://natestoker.com/files';
const RESIZABLE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

export function isSupabaseStorageUrl(url: string): boolean {
  if (!url) return false;
  return url.includes(`${SUPABASE_PROJECT_REF}.supabase.co/storage/v1/object/public/`);
}

export function isEdgeFunctionUrl(url: string): boolean {
  if (!url) return false;
  // Any project's files function, so content saved before the move still resolves.
  return /\.supabase\.co\/functions\/v1\/files\//.test(url);
}

function isLegacyCleanUrl(url: string): boolean {
  if (!url) return false;
  return url.includes('natestoker.com/files/');
}

/**
 * Extract the file path from any recognized URL format.
 */
function extractFilePath(url: string): string | null {
  if (isSupabaseStorageUrl(url)) {
    const match = url.match(/\/storage\/v1\/object\/public\/([^?]+)/);
    if (!match) return null;
    const fullPath = match[1];
    const slashIndex = fullPath.indexOf('/');
    if (slashIndex === -1) return null;
    const bucket = fullPath.substring(0, slashIndex);
    if (bucket !== 'media') return null;
    return fullPath.substring(slashIndex + 1);
  }

  if (isEdgeFunctionUrl(url)) {
    const match = url.match(/\/functions\/v1\/files\/(.+)/);
    return match ? match[1] : null;
  }

  if (isLegacyCleanUrl(url)) {
    const match = url.match(/natestoker\.com\/files\/(.+)/);
    return match ? match[1] : null;
  }

  return null;
}

/**
 * Convert to the site's file URL (natestoker.com/files/<path>).
 * Used for runtime file loading: img src, a href, iframe src, etc.
 */
export function toCleanUrl(supabaseUrl: string): string {
  if (!supabaseUrl) return supabaseUrl;
  const filePath = extractFilePath(supabaseUrl);
  if (filePath) return `${FILES_BASE_URL}/${filePath}`;
  return supabaseUrl;
}

/**
 * Same as toCleanUrl. Kept for the admin "Copy Clean URL" features.
 */
export function toBrandedUrl(supabaseUrl: string): string {
  return toCleanUrl(supabaseUrl);
}

/**
 * Generate a file URL from a path (edge function URL, works everywhere).
 */
export function getFileUrl(path: string): string {
  if (!path) return '';
  const cleanPath = path.replace(/^\/+/, '');
  return `${FILES_BASE_URL}/${cleanPath}`;
}

/**
 * Generate a resized image URL for responsive images (srcset).
 * Returns the original URL for non-media, non-image, or untransformable files.
 */
export function getTransformUrl(url: string, width: number, quality = 80): string {
  if (!url || IMAGE_TRANSFORMS === 'off') return url;

  const filePath = extractFilePath(url);
  if (!filePath) return url;

  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  if (!RESIZABLE_EXTENSIONS.includes(ext)) return url;

  if (IMAGE_TRANSFORMS === 'on') {
    return `https://${SUPABASE_PROJECT_REF}.supabase.co/storage/v1/render/image/public/media/${filePath}?width=${width}&quality=${quality}&resize=contain`;
  }
  return `${FILES_BASE_URL}/${filePath}?w=${width}`;
}

/**
 * Transform all Supabase storage URLs in an HTML string to natestoker.com/files URLs.
 */
export function transformHtmlUrls(html: string): string {
  if (!html) return html;
  const supabaseUrlPattern = new RegExp(
    `https://${SUPABASE_PROJECT_REF}\\.supabase\\.co/storage/v1/object/public/[^"'\\s<>]+`,
    'g'
  );
  return html.replace(supabaseUrlPattern, (match) => toCleanUrl(match));
}
